import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

import { extendCanvasWithFamlaBranding, stampFamlaPdfImageMetadata } from "../../utils/exportBranding";
import { Line } from "../components/Swimlane/types";
import { computeAdaptiveExportScale } from "./exportScaleUtils";

export { computeAdaptiveExportScale } from "./exportScaleUtils";

/** Logo coin cartographie — compact mais lisible (style Gemini). */
const SWIMLANE_EXPORT_BRANDING_SCALE = 1;

const PDF_JPEG_THRESHOLD_PX = 4_000_000;
const SELECTION_SHADOW_PATTERN = /59,\s*130,\s*246|3b82f6/i;
const SIDE_HEADERS_PAUSED_KEY = "_sideHeadersScrollPaused";

function findScrollContainer(element: HTMLElement): HTMLElement | null {
  let current: HTMLElement | null = element.parentElement;
  while (current) {
    const style = window.getComputedStyle(current);
    if (
      style.overflowX === "scroll" ||
      style.overflowX === "auto" ||
      style.overflowY === "scroll" ||
      style.overflowY === "auto"
    ) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

/** Remet les bandeaux rôle/contributeur à gauche (translateX sticky annulé). */
export function resetSideHeadersForExport(root: ParentNode): void {
  root.querySelectorAll(".side-header").forEach((el) => {
    const node = el as HTMLElement;
    node.style.setProperty("transform", "none", "important");
  });
  root.querySelectorAll(".side-header .context-menu-trigger").forEach((el) => {
    (el as HTMLElement).style.display = "none";
  });
}

/** Bloque le repositionnement sticky JS pendant la capture html2canvas. */
export function pauseSideHeaderSticky(root: HTMLElement): () => void {
  (root as HTMLElement & { [SIDE_HEADERS_PAUSED_KEY]?: boolean })[SIDE_HEADERS_PAUSED_KEY] = true;
  resetSideHeadersForExport(root);
  return () => {
    delete (root as HTMLElement & { [SIDE_HEADERS_PAUSED_KEY]?: boolean })[SIDE_HEADERS_PAUSED_KEY];
  };
}

/**
 * Scroll + side-headers sticky → position d'export neutre.
 * Retourne une fonction de restauration à appeler en finally.
 */
export function resetStickyLayoutForExport(root: HTMLElement): () => void {
  const scrollContainer = findScrollContainer(root);
  const savedScroll = scrollContainer
    ? { left: scrollContainer.scrollLeft, top: scrollContainer.scrollTop }
    : null;

  const sideHeaders = Array.from(root.querySelectorAll(".side-header")) as HTMLElement[];
  const savedTransforms = sideHeaders.map((node) => node.style.transform);
  const savedContextMenus = Array.from(
    root.querySelectorAll(".side-header .context-menu-trigger"),
  ).map((el) => (el as HTMLElement).style.display);

  const unpauseSticky = pauseSideHeaderSticky(root);

  if (scrollContainer) {
    scrollContainer.scrollLeft = 0;
    scrollContainer.scrollTop = 0;
  }
  resetSideHeadersForExport(root);
  scrollContainer?.dispatchEvent(new Event("scroll"));

  return () => {
    unpauseSticky();
    if (scrollContainer && savedScroll) {
      scrollContainer.scrollLeft = savedScroll.left;
      scrollContainer.scrollTop = savedScroll.top;
    }
    sideHeaders.forEach((node, index) => {
      if (savedTransforms[index]) {
        node.style.transform = savedTransforms[index];
      } else {
        node.style.removeProperty("transform");
      }
    });
    root.querySelectorAll(".side-header .context-menu-trigger").forEach((el, index) => {
      (el as HTMLElement).style.display = savedContextMenus[index] || "";
    });
    scrollContainer?.dispatchEvent(new Event("scroll"));
  };
}

/** Retire les cadres bleus de sélection, menus et poignées de redimensionnement. */
export function stripEditorChromeForExport(root: ParentNode): void {
  root.querySelectorAll('[class^="block-selection-menu"]').forEach((el) => el.remove());
  root.querySelectorAll(".block-resize-handle").forEach((el) => el.remove());
  root.querySelectorAll('.connection-point, [class*="connection-point"]').forEach((el) => el.remove());

  root.querySelectorAll(".block-selected").forEach((el) => {
    el.classList.remove("block-selected");
    const node = el as HTMLElement;
    node.style.boxShadow = "none";
    node.style.outline = "none";
  });

  root
    .querySelectorAll(
      '[class*="-block-container-"], .process-block, .decision-block, .start-block, .end-block, .note-block',
    )
    .forEach((el) => {
      const node = el as HTMLElement;
      const inlineShadow = node.style.boxShadow || "";
      if (SELECTION_SHADOW_PATTERN.test(inlineShadow)) {
        node.style.boxShadow = "none";
      }
    });

  resetSideHeadersForExport(root);
}

function lightPrepareClone(cloned: HTMLElement): void {
  cloned.style.transform = "scale(1)";
  cloned.style.transformOrigin = "top left";
  stripEditorChromeForExport(cloned);
}

const getCurrentScale = (element: HTMLElement): number => {
  const transform = element.style.transform || window.getComputedStyle(element).transform;
  if (transform && transform !== "none") {
    const scaleMatch = transform.match(/scale\(([^)]+)\)/);
    if (scaleMatch) {
      return parseFloat(scaleMatch[1]) || 1;
    }
    const matrixMatch = transform.match(/matrix\(([^)]+)\)/);
    if (matrixMatch) {
      const values = matrixMatch[1].split(",").map((v) => parseFloat(v.trim()));
      if (values.length >= 1) {
        return values[0] || 1;
      }
    }
  }
  return 1;
};

async function yieldToMain(): Promise<void> {
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  await new Promise<void>((resolve) => setTimeout(resolve, 0));
}

async function captureDiagramCanvas(element: HTMLElement): Promise<HTMLCanvasElement> {
  const originalTransform = element.style.transform;
  const originalTransformOrigin = element.style.transformOrigin;
  const restoreStickyLayout = resetStickyLayoutForExport(element);

  try {
    const currentScale = getCurrentScale(element);
    element.style.transform = "scale(1)";
    element.style.transformOrigin = "top left";
    stripEditorChromeForExport(element);

    await yieldToMain();
    await new Promise((resolve) => setTimeout(resolve, 80));

    const width = element.scrollWidth;
    const height = element.scrollHeight;
    const exportScale = computeAdaptiveExportScale(width, height, currentScale);

    return await html2canvas(element, {
      scale: exportScale,
      useCORS: true,
      allowTaint: false,
      backgroundColor: "#ffffff",
      logging: false,
      width,
      height,
      windowWidth: width,
      windowHeight: height,
      imageTimeout: 15000,
      foreignObjectRendering: false,
      onclone: (_clonedDoc, clonedElement) => {
        lightPrepareClone(clonedElement as HTMLElement);
      },
    });
  } finally {
    element.style.transform = originalTransform;
    element.style.transformOrigin = originalTransformOrigin;
    restoreStickyLayout();
  }
}

function downloadCanvasPng(canvas: HTMLCanvasElement, fileName: string): Promise<void> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("PNG export failed"));
          return;
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        resolve();
      },
      "image/png",
      0.92,
    );
  });
}

/** Exporte un conteneur HTML en image PNG. */
export const exportToPng = async (containerRef: React.RefObject<HTMLDivElement>): Promise<void> => {
  if (!containerRef.current) return;

  try {
    await yieldToMain();
    const canvas = await captureDiagramCanvas(containerRef.current);
    await yieldToMain();
    const branded = await extendCanvasWithFamlaBranding(canvas, {
      brandingScale: SWIMLANE_EXPORT_BRANDING_SCALE,
    });
    await downloadCanvasPng(branded, "swimlane-diagram.png");
  } catch (error) {
    console.error("Erreur lors de l'exportation en PNG:", error);
  }
};

/** Exporte un conteneur HTML en PDF. */
export const exportToPdf = async (containerRef: React.RefObject<HTMLDivElement>): Promise<void> => {
  if (!containerRef.current) return;

  try {
    await yieldToMain();
    const canvas = await captureDiagramCanvas(containerRef.current);
    await yieldToMain();
    const branded = await extendCanvasWithFamlaBranding(canvas, {
      brandingScale: SWIMLANE_EXPORT_BRANDING_SCALE,
    });

    const pixelCount = branded.width * branded.height;
    const useJpeg = pixelCount > PDF_JPEG_THRESHOLD_PX;
    const imgData = branded.toDataURL(useJpeg ? "image/jpeg" : "image/png", useJpeg ? 0.9 : 1);
    const mmWidth = (branded.width / 96) * 25.4;
    const mmHeight = (branded.height / 96) * 25.4;

    const pdf = new jsPDF({
      orientation: mmWidth > mmHeight ? "landscape" : "portrait",
      unit: "mm",
      format: [mmWidth, mmHeight],
      compress: true,
    });

    stampFamlaPdfImageMetadata(pdf, "Process map");
    pdf.addImage(imgData, useJpeg ? "JPEG" : "PNG", 0, 0, mmWidth, mmHeight, undefined, "FAST");
    pdf.save("swimlane-diagram.pdf");
  } catch (error) {
    console.error("Erreur lors de l'exportation en PDF:", error);
  }
};

/** Exporte les données sous forme de fichier JSON. */
export const exportToJson = (data: {
  swimlane: any;
  nodes: any[];
  lines: Line[];
}): void => {
  const jsonString = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonString], { type: "application/json" });
  downloadBlob(blob, "swimlane-diagram.json");
};

const downloadBlob = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
